import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
  type CanActivateFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { LocalStorageService } from '@cosmos/data-access-storage';
import { injectWindow } from '@cosmos/util-common/di';
import {
  CustomerPortalAuthActions,
  CustomerPortalAuthQueries,
  isPresentationUuid,
} from '@customer-portal/auth/data-access-auth';
import { dispatchPdfDocumentError } from '@esp/common/util-pdf-generation';

import {
  getParams,
  handleLoginErrorRedirect,
  tryToRestorePresentationSessionFromStorage,
} from './access.guard.util';

/**
 * This guard is used to handle the authentication for direct access to a presentation print page.
 */
export const customerPortalPresentationPrintAuthGuard: CanActivateFn =
  async function (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<UrlTree | boolean> {
    const store = inject(Store);
    const router = inject(Router);
    const lsService = inject(LocalStorageService);
    const window = injectWindow();
    const { accessCode, token, presentationId } = getParams(route);
    const isUuid = isPresentationUuid(presentationId);

    const session = store.selectSnapshot(CustomerPortalAuthQueries.getSession);
    const isUserDistributor = store.selectSnapshot(
      CustomerPortalAuthQueries.getUserIsDistributor
    );
    // Already authenticated with no explicit credential override (except for UUID-identified presentations).
    // A distributor session is exempt from the uuid carve-out — see
    // `presentation-auth.guard.ts` for why re-logging in would downgrade it.
    if (session && !token && !accessCode && (isUserDistributor || !isUuid)) {
      return true;
    }

    if (token) {
      try {
        await firstValueFrom(
          store.dispatch(
            new CustomerPortalAuthActions.LoginWithDistributorToken({
              distributorToken: token,
            })
          ),
          { defaultValue: undefined }
        );
      } catch (error) {
        // The page won't render — abort the PDF worker instead of letting it
        // wait for the ready selector until it times out.
        dispatchPdfDocumentError(window, error, 401);
        throw error;
      }
      return true;
    }

    // Try to restore presentation session from local storage in case of:
    // - The user has no active session and no access code is provided in the URL.
    // - The presentation is identified by a UUID.
    if ((!session && !accessCode) || isUuid) {
      if (
        await tryToRestorePresentationSessionFromStorage(
          store,
          lsService,
          route
        )
      ) {
        // if session is restored we can redirect to the actual presentation url
        return true;
      }
    }

    if (accessCode || isUuid) {
      try {
        await firstValueFrom(
          store.dispatch(
            new CustomerPortalAuthActions.LoginWithAccessCode({
              accessCode: isUuid ? undefined : accessCode,
              presentationId: presentationId,
            })
          ),
          { defaultValue: undefined }
        );
        return true;
      } catch (error) {
        dispatchPdfDocumentError(window, error, 401);
        return handleLoginErrorRedirect(error, router);
      }
    }

    // At this moment we don't have session and there are credentials to login

    // TODO(dt): what should we do if there is no access code?
    dispatchPdfDocumentError(window, null, 401);
    return router.navigate(['/unauthorized'], { skipLocationChange: true });
  };
